import { createBrowserRouter, Outlet, ScrollRestoration } from "react-router-dom";
import Book from "react-feather/dist/icons/book";
import Calendar from "react-feather/dist/icons/calendar";
import Camera from "react-feather/dist/icons/camera";
import Printer from "react-feather/dist/icons/printer";
import Truck from "react-feather/dist/icons/truck";
import DollarSign from "react-feather/dist/icons/dollar-sign";

import Landing from "./landing";
import AppLayout from "./app-layout";
import Upload from "./upload";
import Settings from "./settings";
import Customize from "./customize";
import Shipping from "./shipping";
import Confirmation from "./confirmation";
import Privacy from "./privacy";
import DataDeletion from "./data-deletion";
import AuthCallback from "./auth-callback";
import About from "./about";
import Admin from "./admin";
import AdminProductEdit from "./admin/product-edit";
import Account from "./account";
import FAQ from "./faq";
import StartProduct from "./start-product";

// Use the same background for now - can be replaced with product-specific images
import backgroundImage from "./frontpage/background.jpg";

// Root layout with scroll restoration
function RootLayout() {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
}

// Shared features
const sharedFeatures = [
  {
    icon: Camera,
    title: "Facile à créer",
    description: "Uploadez vos photos et personnalisez votre produit en quelques clics",
  },
  {
    icon: Printer,
    title: "Qualité premium",
    description: "Impression haute qualité sur papier épais et couverture rigide",
  },
  {
    icon: Truck,
    title: "Livraison partout",
    description: "Livraison rapide partout en Algérie, directement chez vous",
  },
  {
    icon: DollarSign,
    title: "Prix accessible",
    description: "Des tarifs compétitifs pour immortaliser vos souvenirs",
  },
];

// Main Homepage
function MainLanding() {
  return (
    <Landing
      seo={{
        title: "Lymaprint - Livre photo et calendrier personnalisé en Algérie",
        description:
          "Créez votre livre photo ou calendrier personnalisé en quelques clics. Impression de qualité, livraison partout en Algérie.",
        canonicalUrl: "/",
        structuredData: {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Lymaprint",
          description:
            "Service d'impression de livres photo et calendriers personnalisés en Algérie",
          url: "https://lymaprint.com",
        },
      }}
      hero={{
        headline: "Vos souvenirs méritent d'être imprimés",
        tagline: "Livre photo et calendrier personnalisé, livré partout en Algérie",
        ctas: [
          { label: "Créer un livre photo", to: "/start/photo-book", icon: Book },
          { label: "Créer un calendrier", to: "/start/calendar", icon: Calendar },
        ],
        backgroundImage: backgroundImage,
      }}
      features={sharedFeatures}
    />
  );
}

// Photo Book Landing
function PhotoBookLanding() {
  return (
    <Landing
      seo={{
        title: "Livre Photo Personnalisé - Lymaprint",
        description:
          "Créez un livre photo unique avec vos plus beaux souvenirs. Qualité premium, livraison rapide en Algérie. À partir de 2900 DA.",
        canonicalUrl: "/photo-book",
        structuredData: {
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Livre Photo Personnalisé",
          description: "Livre photo de qualité premium avec vos photos personnelles",
          brand: { "@type": "Brand", name: "Lymaprint" },
          offers: {
            "@type": "Offer",
            price: "2900",
            priceCurrency: "DZD",
            availability: "https://schema.org/InStock",
          },
        },
      }}
      hero={{
        headline: "Créez votre livre photo",
        tagline: "Imprimez vos plus beaux souvenirs dans un livre de qualité premium",
        ctas: [{ label: "Commencer maintenant", to: "/start/photo-book", icon: Book }],
        backgroundImage: backgroundImage,
      }}
      features={sharedFeatures}
      pricing={{
        price: 2900,
        priceLabel: "À partir de",
        currency: "DA",
        includes: ["20 pages incluses", "Couverture personnalisée", "Papier photo brillant"],
        ctaLabel: "Créer mon livre photo",
        ctaTo: "/start/photo-book",
      }}
    />
  );
}

// Calendar Landing
function CalendarLanding() {
  return (
    <Landing
      seo={{
        title: "Calendrier Photo Personnalisé 2026 - Lymaprint",
        description:
          "Créez votre calendrier photo personnalisé pour 2026. 12 mois de souvenirs, livré chez vous en Algérie. 3500 DA.",
        canonicalUrl: "/calendar",
        structuredData: {
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Calendrier Photo Personnalisé 2026",
          description: "Calendrier mural personnalisé avec vos photos pour l'année 2026",
          brand: { "@type": "Brand", name: "Lymaprint" },
          offers: {
            "@type": "Offer",
            price: "3500",
            priceCurrency: "DZD",
            availability: "https://schema.org/InStock",
          },
        },
      }}
      hero={{
        headline: "Créez votre calendrier 2026",
        tagline: "12 mois de souvenirs personnalisés, un cadeau unique",
        ctas: [
          {
            label: "Commencer maintenant",
            to: "/start/calendar",
            icon: Calendar,
          },
        ],
        backgroundImage: backgroundImage,
      }}
      features={sharedFeatures}
      pricing={{
        price: 3500,
        priceLabel: "",
        currency: "DA",
        includes: ["12 mois + couverture", "Format A4", "Papier haute qualité"],
        ctaLabel: "Créer mon calendrier",
        ctaTo: "/start/calendar",
      }}
    />
  );
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: "/", element: <MainLanding /> },
      { path: "/photo-book", element: <PhotoBookLanding /> },
      { path: "/calendar", element: <CalendarLanding /> },
      { path: "/privacy", element: <Privacy /> },
      { path: "/data-deletion", element: <DataDeletion /> },
      { path: "/auth/email-callback", element: <AuthCallback /> },
      { path: "/about", element: <About /> },
      { path: "/start/:name", element: <StartProduct /> },
      { path: "/faq", element: <FAQ /> },
      {
        element: <AppLayout />,
        children: [
          { path: "/account/:name", element: <Account /> },
          { path: "/upload/:uid", element: <Upload /> },
          { path: "/settings/:uid", element: <Settings /> },
          { path: "/customize/:uid", element: <Customize /> },
          { path: "/shipping/:uid", element: <Shipping /> },
          { path: "/confirmation/:uid", element: <Confirmation /> },
        ],
      },
      {
        path: "/admin",
        element: <Admin />,
        children: [
          { index: true, element: null },
          { path: ":uid", element: null },
        ],
      },
      { path: "/admin/:uid/edit", element: <AdminProductEdit /> },
    ],
  },
]);
